import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import PropTypes from 'prop-types';

import withPage from '../../page';
import WithCompare from './compare-container';
import Button from '../../ui/components/button';
import CompareTable from './compare-table';
import { Grid, Column } from '../../ui/components/grid';
import CompareBox from './compare-box';
import CompareBoxDetail from './compare-box-detail';
import Link from '../../ui/components/link';
import Icon from '../../ui/components/icon';

import './compare-view.less';

function CompareView(props) {
    const {
        compareProducts,
        history,
        toggleCompareProduct,
        compareResultsV2: compareResults,
        compareSlots,
        match,
    } = props;
    const { params } = match;
    const { type, id } = params;
    const [isFirstHalf, setIsFirstHalf] = useState(true);
    const productsView = useRef(null);
    const readOnly = type === 'config';

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const monitorScroll = () => {
        const { scrollWidth, scrollLeft } = productsView.current;
        setIsFirstHalf(scrollLeft / scrollWidth > 0.4);
    };

    useEffect(() => {
        if (type === 'config' && id) {
            props.getConfigCompare(id);
        }
    }, [type, id]);

    useEffect(() => {
        const scrollView = document.getElementById('compare-view-horizontal-scroll');
        scrollView.addEventListener('scroll', monitorScroll);
        return () => scrollView.removeEventListener('scroll', monitorScroll);
    }, []);

    useEffect(() => {
        if (type !== 'config') {
            props.getProductCompare();
        }
    }, [compareProducts.length]);

    if (compareProducts.length === 1) {
        history.goBack();
    }

    const emptyItems = Array.from(Array(compareSlots - compareProducts.length).keys());

    const hideClass = `${tooltipOpen ? ' hide' : ''}`;

    return (
        <ScrollSync vertical={false} proportional={false}>
            <div className="compare-view">
                <div className={`top-bar${hideClass}`}>
                    <div className="container">
                        <span className="back" onClick={() => history.goBack()}>
                            <Icon name="left_small" />
                            BACK
                        </span>
                        <span className="count">
                            {compareProducts.length}/{compareSlots}
                        </span>
                        <Button
                            text="close"
                            color="white"
                            action="remove"
                            size="small"
                            onClick={() => history.goBack()}
                        />
                    </div>
                </div>
                <ScrollSyncPane>
                    <div
                        id="compare-view-horizontal-scroll"
                        className={`horizontal-scroll fixed${hideClass}`}
                        data-scrollsync
                        ref={productsView}
                    >
                        <Grid className="compare">
                            <Column desktop={2} hide={['small', 'xs', 'xxs']} />
                            {[...compareProducts]
                                .slice()
                                .reverse()
                                .map((product, i) => (
                                    <Column key={i} desktop={2} small={2} mobile={2}>
                                        <CompareBox
                                            product={product}
                                            toggleCompareProduct={products => toggleCompareProduct(products, false)}
                                            readOnly={readOnly}
                                        />
                                    </Column>
                                ))}
                            {emptyItems.map(k => (
                                <Column key={k} className="compare-box" desktop={2} small={2} mobile={2} />
                            ))}
                            <Column className="actions" desktop={2} hide={['small', 'xs', 'xxs']}>
                                <span className="count">
                                    {compareProducts.length}/{compareSlots}
                                </span>
                                <Button
                                    text="close"
                                    action="remove"
                                    color="white"
                                    size="small"
                                    onClick={() => history.goBack()}
                                />
                            </Column>
                        </Grid>
                    </div>
                </ScrollSyncPane>
                <div className={`bottom-bar fixed${hideClass}`}>
                    <span className={`bar${isFirstHalf ? '' : ' active'}`} />
                    <span className={`bar${isFirstHalf ? ' active' : ''}`} />
                </div>
                <ScrollSyncPane>
                    <div className="horizontal-scroll wrapper" data-scrollsync>
                        <div className="content">
                            <div className={`cardContainer${hideClass}`}>
                                <Grid className="product-cards">
                                    <Column desktop={2} hide={['small', 'xs', 'xxs']} />
                                    {[...compareProducts]
                                        .slice()
                                        .reverse()
                                        .map((product, i) => (
                                            <Column key={i} desktop={2} small={2} mobile={2}>
                                                <CompareBoxDetail
                                                    product={product}
                                                    toggleCompareProduct={products =>
                                                        toggleCompareProduct(products, false)
                                                    }
                                                    readOnly={readOnly}
                                                />
                                            </Column>
                                        ))}
                                    {emptyItems.map(k => (
                                        <Column
                                            key={k}
                                            className={`compare-box ${k === 0 ? 'add-new' : 'empty'}`}
                                            desktop={2}
                                            small={2}
                                            mobile={2}
                                        >
                                            {!readOnly && (
                                                <span>
                                                    {k === 0 && (
                                                        <Link button onClick={() => history.goBack()}>
                                                            add new similar
                                                        </Link>
                                                    )}
                                                </span>
                                            )}
                                        </Column>
                                    ))}
                                    <Column desktop={2} hide={['small', 'xs', 'xxs']} />
                                </Grid>
                            </div>
                            {/* SAMPLE TEMPLATE until we get data */}
                            <div className="container">
                                {compareResults && (
                                    <CompareTable
                                        compareProducts={compareProducts}
                                        compareResults={compareResults}
                                        onTooltipOpen={open => setTooltipOpen(open)}
                                    />
                                )}
                            </div>
                            {/* <CompareModal {...props} /> */}
                        </div>
                    </div>
                </ScrollSyncPane>
            </div>
        </ScrollSync>
    );
}

CompareView.defaultProps = {
    compareSlots: 4,
    compareResultsV2: null,
};

CompareView.propTypes = {
    /** list of products to compare */
    compareProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
    /** add/remove products from compare list */
    toggleCompareProduct: PropTypes.func.isRequired,
    /** compare attribute details of products list */
    compareResultsV2: PropTypes.arrayOf(PropTypes.object),
    /** available slots to display for product comparisons */
    compareSlots: PropTypes.number,
};

export default withPage(WithCompare(CompareView), {
    disablePageCall: true,
    useV2: true,
});
